import { Global, useTheme, ThemeProvider, css } from '@emotion/react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import getCssStyle from '../styles/style';
import DEFAULT_THEME from '../styles/default_variables';
import useResourceLoader from '../util/useResourceLoader';
// Use the in-house Loader (not the one from @akcelita/uwe-components) so the
// app-boot spinner matches every in-page loader — same dual-arc design, same
// size, same accent colour. Previously the package version rendered tiny at
// the top of the viewport while route-level loaders rendered big and centered.
import Loader from '../components/Loader';
import getVariables from '../styles/_variables.css';
import Notification from '../components/Notification';

/**
 * Bridge the resolved tenant theme → CSS custom properties on :root.
 *
 * Two layers of tokens are emitted:
 *   1. Brand palette (colors) — drives `bg-primary`, `text-info`, etc.
 *   2. Tailwind design tokens from theme.tailwind — radius, shadow, spacing,
 *      typography, label and per-customfield overrides. These match the
 *      @theme declarations in tailwind.css, so overriding a CSS var here
 *      instantly re-skins every utility consuming it (no rebuild needed).
 *
 * Persistence shape: ThemeSetting.Extras.tailwind on the backend. Tenants
 * customise via the SystemSettings > Theme Editor UI.
 *
 * Per-customfield overrides are emitted as `--cf-<fieldKey>-<token>` vars so
 * a wrapper element (or the FormBuilder's per-field hook) can consume them
 * without polluting the global token namespace. */
function buildThemeCssVars(theme) {
  const c = theme?.colors || {};
  const f = theme?.font || {};
  const tw = theme?.tailwind || {};
  const radius = tw.radius || {};
  const shadow = tw.shadow || {};
  const spacing = tw.spacing || {};
  const fontSize = tw.fontSize || {};
  const fontFamily = tw.fontFamily || {};
  const fontWeight = tw.fontWeight || {};
  const label = tw.label || {};
  // Section header / fieldset legend tokens — drives every `legend.type-section`
  // in the rendered form. Decoupled from `label` so authors can style the
  // section title independently from the field labels underneath.
  const legend = tw.legend || {};
  const customFields = tw.customFields || {};
  // Tenant-wide form-field defaults. Sits between per-component overrides
  // and the global theme tokens in the useFieldTheme cascade — a single
  // value here re-skins every input/select/checkbox/etc. at once.
  const formField = tw.formField || {};
  // System-wide UI chrome. Each one maps to a small CSS rule in tailwind.css
  // that targets the existing markup the app already produces — so theming
  // these is a token-level concern with no JSX changes.
  const error      = tw.error      || {}; // .error-detail.bs-callout
  const focusRing  = tw.focusRing  || {}; // input:focus / select:focus / textarea:focus
  const pageTitle  = tw.pageTitle  || {}; // h1.page-title / .edit-page-header h1
  const sidebar    = tw.sidebar    || {}; // .nav-menu / .sidebar-menu

  // Scoped to :root:not(.dark) so the tenant theme applies ONLY in light mode.
  // In dark mode the app ignores theme settings entirely and uses the built-in
  // dark palette from the .dark block in tailwind.css (fully-dark, coherent).
  // The :not(.dark) selector also raises specificity (0,2,0) so the tenant
  // theme cleanly beats the base :root/.light defaults in light mode.
  return css`
    :root:not(.dark) {
      /* === Brand palette ============================================ */
      --color-primary:    ${c.primary    || '#091f68'};
      --color-secondary:  ${c.secondary  || '#9dcbef'};
      --color-info:       ${c.info       || '#3c8ef7'};
      --color-success:    ${c.success    || '#4caf50'};
      --color-warning:    ${c.warning    || '#f8d161'};
      --color-danger:     ${c.danger     || '#f44336'};
      --color-main:       ${c.main       || '#000000'};
      --color-background: ${c.background || '#ffffff'};
      /* NOTE: --bg-primary (the app body canvas) is intentionally NOT set
         here. It is defined in @theme as var(--color-background, #fafafa), so
         it follows this --color-background in light mode while the unlayered
         .dark rule still wins in dark mode. Setting it here (emotion :root,
         injected last) would beat .dark and break the dark-mode toggle. */

      /* === Accent (active / hover / selected states) ================
         Drives every theme-accent-* utility (Pagination active page,
         Sidebar active tab, WorkTray filter badge, row hover/selected,
         SwitchBoard tile hover, Button primary/link/blue, …).
         Defaults to color_primary so changing the brand color also re-
         skins all accented states. Tenants can break the link by setting
         extras.tailwind.accent.color explicitly. */
      --accent-color:       ${(tw.accent || {}).color       || c.primary || '#2563eb'};
      --accent-color-hover: ${(tw.accent || {}).colorHover  || c.primary || '#3b82f6'};

      /* RGB variants for opacity-aware utilities, e.g. rgb(var(--color-primary-rgb) / 0.5) */
      --color-primary-rgb: ${hexToRgb(c.primary || '#091f68')};
      --color-info-rgb:    ${hexToRgb(c.info    || '#3c8ef7')};
      --color-success-rgb: ${hexToRgb(c.success || '#4caf50')};
      --color-warning-rgb: ${hexToRgb(c.warning || '#f8d161')};
      --color-danger-rgb:  ${hexToRgb(c.danger  || '#f44336')};

      /* Gray ramp (used by --label-color default and many components) */
      --color-gray-100: ${c['gray-100'] || c.gray100 || '#f5f5f5'};
      --color-gray-200: ${c['gray-200'] || c.gray200 || '#eeeeee'};
      --color-gray-300: ${c['gray-300'] || c.gray300 || '#e0e0e0'};
      --color-gray-400: ${c['gray-400'] || c.gray400 || '#bdbdbd'};
      --color-gray-500: ${c['gray-500'] || c.gray500 || '#9e9e9e'};
      --color-gray-600: ${c['gray-600'] || c.gray600 || '#757575'};
      --color-gray-700: ${c['gray-700'] || c.gray700 || '#616161'};
      --color-gray-800: ${c['gray-800'] || c.gray800 || '#424242'};
      --color-gray-900: ${c['gray-900'] || c.gray900 || '#212121'};

      /* === Tailwind design tokens =================================== */
      ${radius.sm   ? `--radius-sm:  ${radius.sm};`   : ''}
      ${radius.md   ? `--radius-md:  ${radius.md};`   : ''}
      ${radius.lg   ? `--radius-lg:  ${radius.lg};`   : ''}
      ${radius.xl   ? `--radius-xl:  ${radius.xl};`   : ''}
      ${radius['2xl'] ? `--radius-2xl:${radius['2xl']};` : ''}

      ${shadow.sm  ? `--shadow-sm: ${shadow.sm};` : ''}
      ${shadow.md  ? `--shadow-md: ${shadow.md};` : ''}
      ${shadow.lg  ? `--shadow-lg: ${shadow.lg};` : ''}
      ${shadow.xl  ? `--shadow-xl: ${shadow.xl};` : ''}

      ${spacing.base ? `--spacing: ${spacing.base};` : ''}

      /* font-sans / font-mono drive every Tailwind font utility AND get a
         fallback to the legacy fontSettings.font-family so that changing
         "Font" in the existing Theme Editor instantly retypes the app — no
         separate Tailwind override required. */
      --font-sans: ${fontFamily.sans || f.fontFamily || "'Inter', sans-serif"};
      ${fontFamily.mono ? `--font-mono: ${fontFamily.mono};` : ''}

      ${fontSize.xs   ? `--text-xs:   ${fontSize.xs};`   : ''}
      ${fontSize.sm   ? `--text-sm:   ${fontSize.sm};`   : ''}
      ${fontSize.base ? `--text-base: ${fontSize.base};` : ''}
      ${fontSize.lg   ? `--text-lg:   ${fontSize.lg};`   : ''}
      ${fontSize.xl   ? `--text-xl:   ${fontSize.xl};`   : ''}
      ${fontSize['2xl'] ? `--text-2xl: ${fontSize['2xl']};` : ''}

      ${fontWeight.normal ? `--font-weight-normal: ${fontWeight.normal};` : ''}
      ${fontWeight.medium ? `--font-weight-medium: ${fontWeight.medium};` : ''}
      ${fontWeight.bold   ? `--font-weight-bold:   ${fontWeight.bold};`   : ''}

      /* Label (consumed by .theme-label utility in tailwind.css) */
      ${label.fontSize      ? `--label-font-size:      ${label.fontSize};`      : ''}
      ${label.fontWeight    ? `--label-font-weight:    ${label.fontWeight};`    : ''}
      ${label.color         ? `--label-color:          ${label.color};`         : ''}
      ${label.textTransform ? `--label-text-transform: ${label.textTransform};` : ''}
      ${label.letterSpacing ? `--label-letter-spacing: ${label.letterSpacing};` : ''}

      /* Legend / section title (consumed by legend.type-section in tailwind.css) */
      ${legend.fontSize      ? `--legend-font-size:      ${legend.fontSize};`      : ''}
      ${legend.fontWeight    ? `--legend-font-weight:    ${legend.fontWeight};`    : ''}
      ${legend.color         ? `--legend-color:          ${legend.color};`         : ''}
      ${legend.borderColor   ? `--legend-border-color:   ${legend.borderColor};`   : ''}
      ${legend.letterSpacing ? `--legend-letter-spacing: ${legend.letterSpacing};` : ''}
      ${legend.textTransform ? `--legend-text-transform: ${legend.textTransform};` : ''}

      /* Validation errors (.error-detail / .bs-callout) */
      ${error.background  ? `--error-bg:           ${error.background};`  : ''}
      ${error.borderColor ? `--error-border-color: ${error.borderColor};` : ''}
      ${error.color       ? `--error-color:        ${error.color};`       : ''}
      ${error.iconColor   ? `--error-icon-color:   ${error.iconColor};`   : ''}

      /* Focus ring on form inputs */
      ${focusRing.color       ? `--focus-ring-color:        ${focusRing.color};`       : ''}
      ${focusRing.borderColor ? `--focus-ring-border-color: ${focusRing.borderColor};` : ''}

      /* Page title (h1.page-title / .edit-page-header h1) */
      ${pageTitle.fontSize   ? `--page-title-font-size:   ${pageTitle.fontSize};`   : ''}
      ${pageTitle.fontWeight ? `--page-title-font-weight: ${pageTitle.fontWeight};` : ''}
      ${pageTitle.color      ? `--page-title-color:       ${pageTitle.color};`      : ''}
      ${pageTitle.fontFamily ? `--page-title-font-family: ${pageTitle.fontFamily};` : ''}

      /* Sidebar menu (.nav-menu / .sidebar-menu / aside.uwe-sidebar) */
      ${sidebar.background       ? `--sidebar-bg:                ${sidebar.background};`       : ''}
      ${sidebar.itemColor        ? `--sidebar-item-color:        ${sidebar.itemColor};`        : ''}
      ${sidebar.itemHoverBg      ? `--sidebar-item-hover-bg:     ${sidebar.itemHoverBg};`      : ''}
      ${sidebar.itemActiveBg     ? `--sidebar-item-active-bg:    ${sidebar.itemActiveBg};`     : ''}
      ${sidebar.itemActiveColor  ? `--sidebar-item-active-color: ${sidebar.itemActiveColor};`  : ''}
      ${sidebar.borderColor      ? `--sidebar-border-color:      ${sidebar.borderColor};`      : ''}

      /* Top header (header.uwe-app-header). Tokens default to the sidebar's
         values via CSS var fallback in tailwind.css, so unsetting these is
         the same as "header looks like sidebar". */
      ${(tw.header || {}).background       ? `--header-bg:               ${tw.header.background};`       : ''}
      ${(tw.header || {}).textColor        ? `--header-text-color:       ${tw.header.textColor};`        : ''}
      ${(tw.header || {}).borderColor      ? `--header-border-color:     ${tw.header.borderColor};`      : ''}
      ${(tw.header || {}).itemHoverBg      ? `--header-item-hover-bg:    ${tw.header.itemHoverBg};`      : ''}
      ${(tw.header || {}).itemHoverColor   ? `--header-item-hover-color: ${tw.header.itemHoverColor};`   : ''}

      /* Legacy font vars kept for backward compatibility with old components */
      --font-family: ${f.fontFamily || fontFamily.sans || "'Inter', sans-serif"};
      --font-weight: ${f.fontWeight || fontWeight.normal || 400};
      --font-size:   ${f.fontSize   || fontSize.base    || '14px'};

      /* === Form-field global defaults ===============================
         Emitted as --ff-TOKEN. Sits between per-component overrides
         and the per-component built-in defaults in the useFieldTheme
         cascade: cf-X-TOKEN, then ff-TOKEN, then component default,
         so a single value here re-skins every form input at once while
         still allowing per-component overrides to win. */
      ${Object.entries(formField).map(
        ([tokenKey, value]) => `--ff-${tokenKey}: ${value};`
      ).join('\n      ')}

      /* === Per-customfield overrides ================================
         Emitted as --cf-FIELDKEY-TOKEN. The useFieldTheme(fieldKey)
         hook (see formbuilder-v1) reads these with a fallback chain to
         --ff-TOKEN and finally the matching global token. */
      ${Object.entries(customFields).flatMap(([fieldKey, overrides]) =>
        Object.entries(overrides || {}).map(
          ([tokenKey, value]) => `--cf-${fieldKey}-${tokenKey}: ${value};`
        )
      ).join('\n      ')}
    }
  `;
}

function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return '0 0 0';
  if (hex.startsWith('rgb')) return hex;
  const m = hex.replace('#', '').match(/.{1,2}/g);
  if (!m || m.length < 3) return '0 0 0';
  const [r, g, b] = m.map(v => parseInt(v, 16));
  return `${r} ${g} ${b}`;
}

export const UWEThemeContext = createContext();

export function UWEThemeProvider({ children }) {
  const { errorLoadingUweTemplate, loadingUweTemplate, uweThemeContextValue } = useUWEThemeProvider();

  return loadingUweTemplate ? (
    <Loader spinner fullscreen />
  ) : errorLoadingUweTemplate ? (
    <Notification color="danger" className="mt-3">
      Error Loading Application.
    </Notification>
  ) : (
    <UWEThemeContext.Provider value={uweThemeContextValue}>
      <ThemeProvider theme={uweThemeContextValue.uweTheme}>
        <UWEThemeGlobals />
        {children}
      </ThemeProvider>
    </UWEThemeContext.Provider>
  );
}

function useUWEThemeProvider() {
  const [uweTemplate, _loadingUweTemplate, errorLoadingUweTemplate] = useResourceLoader(() => window.uwe_template ?? Promise.resolve(), []);
  const loadingUweTemplate = _loadingUweTemplate !== false;

  const [adhocTemplate, setAdhocTemplate] = useState();

  const uweTheme = useMemo(() => buildTheme(DEFAULT_THEME, uweTemplate, adhocTemplate), [buildTheme, DEFAULT_THEME, uweTemplate, adhocTemplate]);

  const uweThemeContextValue = useMemo(
    () => ({
      uweTheme,
      adhocTemplate,
      setAdhocTemplate,
    }),
    [uweTheme, adhocTemplate, setAdhocTemplate]
  );

  return {
    errorLoadingUweTemplate,
    loadingUweTemplate,
    uweThemeContextValue,
  };
}

function buildTheme(...templates) {
  const mergedTemplate = mergedTemplates(templates);

  let $ = buildInitialVars(mergedTemplate);
  // Preserve the merged inner `theme` (carries the tailwind token tree)
  // BEFORE running `getVariables`, which returns a brand-new `$` object that
  // does NOT spread its input — so without this anchor `theme.tailwind` would
  // be silently dropped and every saved Theme Editor override (formField.*,
  // label.*, customFields.*) would never reach `buildThemeCssVars` on page
  // load.
  const inner = $.theme;
  $ = getVariables($);
  $.theme = inner;

  return $;
}

function mergedTemplates(templates) {
  return {
    colors: templates.reduce((_, template) => {
      Object.assign(_, template?.colors ?? {});

      return _;
    }, {}),
    font: templates.reduce((_, template) => {
      Object.assign(_, template?.font ?? template?.fonts ?? template?.fontSettings ?? {});

      return _;
    }, {}),
    // Tailwind tokens are nested (radius.md, customFields.textInput, …) so
    // we deep-merge per-section rather than overwriting. A tenant that sets
    // only `tailwind.radius.md` keeps every other default intact.
    tailwind: templates.reduce((_, template) => {
      // Backend DTO uses PascalCase (`Extras`), frontend draft uses
      // camelCase (`extras`). Accept both so live preview from the editor
      // and persisted tenant theme both flow through.
      const tw = template?.tailwind
        ?? template?.Extras?.tailwind
        ?? template?.extras?.tailwind
        ?? {};
      // Whitelist of tailwind sub-sections we propagate from each template.
      // ⚠️ Adding a new top-level token group requires adding its key here —
      // otherwise the saved value silently never reaches buildThemeCssVars
      // and the runtime falls back to defaults.
      // `accent` drives every theme-accent-* utility (Login bg, active
      // states, etc.). `button` carries the interaction effect setting
      // (ripple / lift / glow / …). Both were missing from the list,
      // causing the Login panel to render the static fallback blue and the
      // button effects to never propagate to the actual app.
      ['radius', 'shadow', 'spacing', 'fontSize', 'fontFamily', 'fontWeight', 'label', 'legend', 'formField',
       'error', 'focusRing', 'pageTitle', 'sidebar', 'header', 'accent', 'button', 'login'].forEach((k) => {
        if (tw[k]) _[k] = { ..._[k], ...tw[k] };
      });
      if (tw.customFields) {
        _.customFields = _.customFields || {};
        Object.entries(tw.customFields).forEach(([fieldKey, overrides]) => {
          _.customFields[fieldKey] = { ..._.customFields[fieldKey], ...overrides };
        });
      }
      return _;
    }, {}),
    // Tenant-authored raw CSS (Theme Editor → Custom CSS tab). A plain string,
    // NOT a token tree, so it can't go through the tailwind whitelist above.
    // Last template that defines a string wins (persisted value on load, the
    // editor's adhoc draft during live preview). Injected globally by
    // UWEThemeGlobals — see there. Accept Extras/extras casing like tailwind.
    customCss: templates.reduce((acc, template) => {
      const v = template?.Extras?.customCss
        ?? template?.extras?.customCss
        ?? template?.customCss;
      return typeof v === 'string' ? v : acc;
    }, ''),
  };
}

function buildInitialVars(mergedTemplate) {
  return {
    theme: {
      ...mergedTemplate,
      colors: Object.entries(mergedTemplate.colors).reduce((acc, [key, value]) => {
        if (/^color_/.test(key)) {
          key = key.replace(/^color_/, '');
        }

        // Preserve gray-100..900 keys as-is for buildThemeCssVars; only
        // camelCase keys that don't follow the gray-NNN pattern.
        acc[/^gray-/.test(key) ? key : paramCaseToCamelCase(key)] = value;

        return acc;
      }, {}),
      font: Object.entries(mergedTemplate.font).reduce((acc, [key, value]) => {
        acc[paramCaseToCamelCase(key)] = value;

        return acc;
      }, {}),
      // Pass through the merged Tailwind tokens unchanged — buildThemeCssVars
      // consumes them as-is to emit --radius-*, --shadow-*, --cf-*-*, etc.
      tailwind: mergedTemplate.tailwind || {},
    },
  };
}

function paramCaseToCamelCase(text) {
  return text.replace(/-(\w)/g, (_, _1) => _1.toUpperCase());
}

/**
 * Hook for customField components to consume their per-field theme tokens.
 *
 * The 8 themed fields (textInput, textArea, select, checkbox, dateWidget,
 * moneyInput, fileField, signatureField) typically just apply the matching
 * `.cf-<fieldKey>` utility class from tailwind.css — that's enough for the
 * common case (border-radius, padding, font, shadow, border).
 *
 * This hook is the escape hatch for fields that need to BRANCH on theme
 * values in JS (e.g. conditional rendering, math on numbers, passing values
 * to a non-CSS library). Returns the override map as-is from the tenant
 * theme; consumers decide how to interpret each key.
 *
 * Example:
 *   const { radius, padding } = useFieldTheme('textInput');
 *   <input style={{ borderRadius: radius || 'var(--radius-md)' }} ... />
 */
export function useFieldTheme(fieldKey) {
  const ctx = useContext(UWEThemeContext);
  const customFields = ctx?.uweTheme?.theme?.tailwind?.customFields || {};
  return customFields[fieldKey] || {};
}

function UWEThemeGlobals() {
  const theme = useTheme();
  const globalStyle = useMemo(() => getCssStyle(theme), [theme]);
  // Inject tenant colors AND Tailwind design tokens as CSS vars on :root. The
  // tailwind.css @theme block declares the defaults; this overrides them with
  // the tenant's persisted values from ThemeSetting.Extras.tailwind.
  const themeVars = useMemo(() => buildThemeCssVars(theme?.theme || {}), [theme]);

  // Tenant-authored raw CSS from the Theme Editor's Custom CSS tab. In the
  // standalone app this is GLOBAL — no wrapper scope — so authors can reach
  // any part of the UI. Injected last (after themeVars and the base global
  // style) so equal-specificity rules from the tenant win over the defaults.
  const customCss = (theme?.theme?.customCss) || '';

  // Button interaction effect — set as an attribute on <html> so the CSS
  // rules in tailwind.css (`[data-button-effect="…"] .btn-themed:hover …`)
  // can scope by it without us having to thread props through every
  // <Button> instance. "none" (default) leaves the buttons with their
  // original transition-only behaviour.
  //
  // Path is `theme.theme.tailwind.button.effect` — NOT `extras.tailwind` —
  // because mergedTemplates() flattens the `extras.` prefix away during
  // theme build (see the docstring near `inner = $.theme`). Same pattern
  // every other tailwind token uses; reading from `extras.tailwind.*` here
  // silently always resolved to 'none' on page load — the user-visible
  // symptom of "buttons don't react to the effect setting".
  const buttonEffect = (theme?.theme?.tailwind?.button?.effect) || 'none';
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const prev = root.getAttribute('data-button-effect');
    root.setAttribute('data-button-effect', buttonEffect);
    return () => {
      // Restore the previous value on unmount/change so unrelated theme
      // providers (e.g. preview frames) don't leak their setting globally.
      if (prev) root.setAttribute('data-button-effect', prev);
      else root.removeAttribute('data-button-effect');
    };
  }, [buttonEffect]);

  // Form-field interaction effect — same mechanism as the Button effect above
  // but scoped to `.cf-*` form-field classes via the rules in tailwind.css
  // under "Form-field interaction effects". Stored under formField.effect so
  // it sits alongside the other global form-field defaults (radius, padding,
  // fontSize, …) the editor already exposes.
  const formFieldEffect = (theme?.theme?.tailwind?.formField?.effect) || 'none';
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const prev = root.getAttribute('data-form-field-effect');
    root.setAttribute('data-form-field-effect', formFieldEffect);
    return () => {
      if (prev) root.setAttribute('data-form-field-effect', prev);
      else root.removeAttribute('data-form-field-effect');
    };
  }, [formFieldEffect]);

  // Tenant web-font stylesheet — the theme's `fonts.fonts-url` (camelCased to
  // `fontsUrl` by buildInitialVars). Without this the configured font-family
  // (e.g. Fraunces) is set on --font-sans but the font file is never
  // downloaded, so the browser silently falls back to the next family in the
  // stack. We inject a <link rel="stylesheet"> keyed by URL and clean it up on
  // change/unmount so preview frames don't leak their font globally.
  const fontsUrl = (theme?.theme?.font?.fontsUrl) || '';
  useEffect(() => {
    if (typeof document === 'undefined' || !fontsUrl) return undefined;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = fontsUrl;
    link.setAttribute('data-uwe-theme-font', '');
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [fontsUrl]);

  return (
    <>
      <Global styles={themeVars} />
      <Global styles={globalStyle} />
      {/* Raw tenant CSS goes into a plain <style> (not emotion's <Global>) so
          it's injected byte-for-byte — emotion routes styles through stylis,
          which can rewrite or drop arbitrary author CSS (comments, @-rules,
          unusual values). A native <style> at the end of the tree also wins
          ties against the theme tokens above by DOM order. */}
      {customCss ? <style data-uwe-custom-css>{customCss}</style> : null}
    </>
  );
}
